import tw from "#src/utils/tw.ts";
import {
  Children,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

interface Props {
  children: ReactNode;
  className?: string;
}

export default function SlideDown(props: Props) {
  const hasChildren = Children.toArray(props.children).length > 0;
  const contentRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  const [renderedChildren, setRenderedChildren] = useState<ReactNode>(
    props.children
  );
  const [height, setHeight] = useState(hasChildren ? "auto" : "0px");

  useLayoutEffect(() => {
    if (!hasChildren) return;
    setRenderedChildren(props.children);
  }, [hasChildren, props.children]);

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    if (hasChildren) {
      setHeight("auto");
      return;
    }

    if (Children.toArray(renderedChildren).length === 0) {
      setHeight("0px");
      return;
    }

    setHeight("auto");

    frameRef.current = requestAnimationFrame(() => {
      setHeight("0px");
      frameRef.current = null;
    });
  }, [hasChildren, renderedChildren]);

  useEffect(() => {
    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  return (
    <div
      className={tw("overflow-hidden", props.className)}
      onTransitionEnd={(event) => {
        if (event.target !== event.currentTarget) return;

        if (hasChildren) {
          setHeight("auto");
          return;
        }

        setRenderedChildren(null);
      }}
      style={{
        height,
        transition: "height 300ms",
      }}
    >
      <div ref={contentRef}>{renderedChildren}</div>
    </div>
  );
}
